const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/
const YOUTUBE_EMBED_BASE_URL = 'https://www.youtube-nocookie.com/embed'
const YOUTUBE_THUMBNAIL_BASE_URL = 'https://i.ytimg.com/vi'
const EXPLICIT_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i
const SUPPORTED_URL_PROTOCOLS = new Set(['http:', 'https:'])

const ensureProtocol = (rawUrl: string): string =>
  EXPLICIT_SCHEME_PATTERN.test(rawUrl) ? rawUrl : `https://${rawUrl}`

const isSupportedYouTubeHost = (host: string): boolean => {
  const normalizedHost = host.toLowerCase()

  return (
    normalizedHost === 'youtu.be' ||
    normalizedHost === 'youtube.com' ||
    normalizedHost.endsWith('.youtube.com') ||
    normalizedHost === 'youtube-nocookie.com' ||
    normalizedHost.endsWith('.youtube-nocookie.com')
  )
}

const sanitizeYouTubeId = (candidate?: string | null): string | null => {
  if (!candidate) return null
  const cleaned = candidate.trim()
  if (!YOUTUBE_ID_PATTERN.test(cleaned)) return null
  return cleaned
}

export function extractYouTubeVideoId(rawUrl?: string): string | null {
  if (!rawUrl) return null

  const value = rawUrl.trim()
  if (!value) return null

  try {
    const parsed = new URL(ensureProtocol(value))
    if (!SUPPORTED_URL_PROTOCOLS.has(parsed.protocol.toLowerCase())) {
      return null
    }

    const host = parsed.hostname.toLowerCase()
    const pathParts = parsed.pathname.split('/').filter(Boolean)
    const normalizedPathParts = pathParts.map((part) => part.toLowerCase())

    if (host === 'youtu.be') {
      return sanitizeYouTubeId(pathParts[0])
    }

    if (!isSupportedYouTubeHost(host)) {
      return null
    }

    const fromSearch = parsed.searchParams.get('v')
    if (fromSearch) return sanitizeYouTubeId(fromSearch)

    const embedIndex = normalizedPathParts.findIndex((part) => part === 'embed')
    if (embedIndex >= 0) {
      return sanitizeYouTubeId(pathParts[embedIndex + 1] || '')
    }

    const shortsIndex = normalizedPathParts.findIndex(
      (part) => part === 'shorts',
    )
    if (shortsIndex >= 0) {
      return sanitizeYouTubeId(pathParts[shortsIndex + 1] || '')
    }

    const liveIndex = normalizedPathParts.findIndex((part) => part === 'live')
    if (liveIndex >= 0) {
      return sanitizeYouTubeId(pathParts[liveIndex + 1] || '')
    }
  } catch {
    return null
  }

  return null
}

export function isYouTubeUrl(rawUrl?: string): boolean {
  return extractYouTubeVideoId(rawUrl) !== null
}

export function toYouTubeEmbedUrl(
  rawUrl?: string,
  options?: { autoplay?: boolean },
): string | null {
  const id = extractYouTubeVideoId(rawUrl)
  if (!id) return null
  const params = new URLSearchParams({ rel: '0' })
  if (options?.autoplay) {
    params.set('autoplay', '1')
  }
  return `${YOUTUBE_EMBED_BASE_URL}/${id}?${params.toString()}`
}

export function getYouTubeThumbnailUrl(rawUrl?: string): string | null {
  const id = extractYouTubeVideoId(rawUrl)
  if (!id) return null
  return `${YOUTUBE_THUMBNAIL_BASE_URL}/${id}/hqdefault.jpg`
}

export function getPreferredVideoThumbnailUrl(
  rawUrl?: string,
  fallbackThumbnailUrl?: string,
): string | null {
  const youtubeThumbnailUrl = getYouTubeThumbnailUrl(rawUrl)
  if (youtubeThumbnailUrl) return youtubeThumbnailUrl

  const trimmedFallback = fallbackThumbnailUrl?.trim()
  return trimmedFallback ? trimmedFallback : null
}
